/**
 * Shared order types used across global types and order section types.
 * Extracted to break circular dependencies between:
 * - src/types/order.ts (imports IPlan, IMilestone)
 * - src/sections/management/accounts/orders/types/order-types.ts (imports ICustomMilestone)
 */
import moment, { type unitOfTime } from 'moment';

// Describes how frequently billing occurs.
export enum ChoicesBillingFrequencyPlan {
  UNSPECIFIED = 'unspecified', // No specific frequency specified.
  annually = 'annually', // Billing once a year.
  monthly = 'monthly', // Billing once a month.
  triennial = 'triennial', // Billing once every three years.
}

// Enumerates potential payment conditions.
export enum ChoicesPaymentConditionPlan {
  UNSPECIFIED = 'unspecified', // No specific payment condition specified.
  prepay = 'prepay', // Requires payment before service is rendered.
  postpay = 'postpay', // Allows payment after service is rendered.
}

export enum PaymentType {
  INITIAL = 'INITIAL',
  COMMITTED = 'COMMITTED',
  ONETIME = 'ONE-TIME',
}

export enum PaymentStatus {
  PENDING = 'PENDING',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
}

// Defines conditions and terms for a plan.
export interface ITemCondition {
  code: string; // Identifier code for the condition.
  amount: number; // Amount associated with the term.
  unit: unitOfTime.DurationConstructor; // Unit of the term amount.
  customerTermCondition?: string;
  customerBillingFrequency?: ChoicesBillingFrequencyPlan;
}

export interface IPayments {
  amount: number;
  costPrice: number;
  discount: number;
  endPeriod: moment.Moment;
  finalPrice: number;
  margin: number;
  paymentDate: moment.Moment;
  paymentPeriod: number;
  price: number;
  proportionalDays: number;
  startPeriod: moment.Moment;
  status: PaymentStatus;
  subtotal: number;
  total: number;
  totalDiscount: number;
  type: PaymentType;
}

// Represents a plan which can be associated with a subscription.
export interface IPlan {
  _id: string; // Unique identifier for the plan.
  name: string; // Name of the plan.
  catalogName: string; // Name as it appears in catalog listings.
  type: string; // Type of the plan.
  splitTerm: boolean; // Whether the term can be split.
  customerBillingFrequency: ChoicesBillingFrequencyPlan; // Billing frequency for the customer.
  providerBillingFrequency: ChoicesBillingFrequencyPlan; // Billing frequency for the provider.
  paymentCondition: ChoicesPaymentConditionPlan; // Payment condition.
  customerTermCondition: ITemCondition; // Customer-specific terms.
  providerTermCondition: ITemCondition; // Provider-specific terms.
  listPrice?: number; // Optional listed price.
  listCost?: number; // Optional listed cost.
  listMargin?: number; // Optional listed margin.
}

// Represents a billing milestone within a subscription or order item.
export interface IMilestone {
  _id?: string;
  paymentDate: moment.Moment;
  milestonePeriod: number;
  payments: IPayments[];
  type: PaymentType;
  totalDiscount: number;
  subtotal: number;
  total: number;
  startPeriod: Date;
  endPeriod: Date;
}

export interface ICustomMilestone {
  id: string;
  paymentDate: moment.Moment;
  amount: number;
  comment: string;
}
